import {Checkbox, Icon} from '@/components/atoms'
import {LoadingSpinner, Pagination, Search} from 'pepsico-ds'
import {useState} from 'react'

/**
 * A reusable list component with selectable items, search functionality, and customizable display options.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.title='List'] - The title displayed at the top of the list
 * @param {Array<Object>} [props.items=[]] - Array of items to display in the list
 * @param {number|string} props.items[].id - Unique identifier for each item
 * @param {string} props.items[].label - Main text label for the item
 * @param {string} [props.items[].subLabel] - Optional secondary label (displayed in blue)
 * @param {boolean} [props.items[].checked=false] - Whether the item's checkbox is checked
 * @param {boolean} [props.items[].selected=false] - Whether the item row is selected (highlighted)
 * @param {Function} [props.onItemClick] - Callback when an item row is clicked (only when selectable=true)
 * @param {Function} [props.onCheckboxChange] - Callback when a checkbox state changes
 * @param {string} [props.searchPlaceholder='Search...'] - Placeholder text for the search input
 * @param {boolean} [props.showSearch=true] - Whether to display the search bar
 * @param {string} [props.className=''] - Additional CSS classes to apply to the container
 * @param {boolean} [props.separateItems=false] - Whether to show separators between all items
 * @param {boolean} [props.selectable=true] - Whether items can be clicked for selection (shows chevron icon)
 * @param {boolean} [props.showSelectedCount=false] - Whether to display count of selected items
 * @param {boolean} [props.showPagination=false] - Whether to show pagination controls
 * @param {boolean} [props.selectedCount=0] - Describes number of selected items
 * @param {number} [props.currentPage=0] - Current page number (0-indexed)
 * @param {number} [props.totalPages=0] - Total number of pages
 * @param {Function} [props.onPageChange] - Callback when page changes
 * @param {boolean} [props.useExternalSearch=false] - Whether to use external search (disables built-in filtering)
 * @param {Function} [props.onSearchChange] - Callback when search input changes (for external search)
 * @param {boolean} [props.isLoading=false] - Whether the list is in a loading state
 *
 * @example
 * // Basic usage with selectable items
 * <SelectableList
 *   title="Folder"
 *   items={folderItems}
 *   onItemClick={handleItemClick}
 *   onCheckboxChange={handleCheckboxChange}
 * />
 *
 * @example
 * // Checkbox-only list with separators and item count
 * <SelectableList
 *   title="Segments"
 *   items={segmentItems}
 *   selectable={false}
 *   separateItems={true}
 *   showSelectedCount={true}
 *   onCheckboxChange={handleCheckboxChange}
 * />
 *
 * @example
 * // List with pagination
 * <SelectableList
 *   title="Segments"
 *   items={segmentItems}
 *   showPagination={true}
 *   currentPage={0}
 *   totalPages={5}
 *   onPageChange={handlePageChange}
 *   pageSize={10}
 *   totalItems={50}
 * />
 */
const SelectableList = ({
  title = 'List',
  items = [],
  onItemClick = () => {},
  onCheckboxChange = () => {},
  searchPlaceholder = 'Search...',
  showSearch = true,
  className = '',
  separateItems = false, // Boolean to add separator after each item
  selectable = true, // Boolean to determine if items are clickable
  showSelectedCount = false, // Boolean to show selected items count
  showPagination = false, // Boolean to show pagination controls
  selectedCount = 0,
  currentPage = 0,
  totalPages = 0,
  onPageChange = () => {},
  useExternalSearch = false, // Whether to use external search (API-based)
  onSearchChange = () => {}, // Callback for external search
  isLoading = false, // Loading state
}) => {
  const [searchTerm, setSearchTerm] = useState('')

  // Filter items based on search term (only if not using external search)
  const filteredItems = useExternalSearch
    ? items // When using external search, items are already filtered by the API
    : items?.filter(item => item?.label?.toLowerCase().includes(searchTerm?.toLowerCase()))

  return (
    <div className={`flex flex-col p-4 text-white ${className}`}>
      {/* Header */}
      {title && <h2 className="mb-4 text-[28px] font-bold">{title}</h2>}

      {/* Selected Count */}
      {showSelectedCount && (
        <div className="mb-3 border-b-2 border-gray-700 pb-3 text-sm text-gray-300">
          {selectedCount} item{selectedCount !== 1 ? '(s)' : ''} selected
        </div>
      )}

      {/* Search Bar */}
      {showSearch && (
        <Search
          className="mb-4 w-full !max-w-full"
          onUpdate={value => {
            setSearchTerm(value)
            if (useExternalSearch) {
              // For external search, call the callback
              onSearchChange(value)
            }
          }}
          placeholder={searchPlaceholder}
          // value={searchTerm}
          onClear={() => setSearchTerm('')}
        />
      )}

      {/* List Items */}
      <div className="space-y-1 ">
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <span className="text-gray-400">
              {searchTerm ? 'No items found matching your search' : 'No items available'}
            </span>
          </div>
        ) : (
          filteredItems.map((item, index) => (
            <div key={item.id || index}>
              <div
                className={`
                flex cursor-pointer items-center justify-between rounded p-2 transition-colors
                ${selectable && item?.selected ? 'bg-gray-800' : ''}
                ${!item?.selected ? 'hover:bg-gray-800' : ''}
              `}
                role="button"
                tabIndex={0}
                onClick={() => {
                  if (selectable) {
                    onItemClick(item)
                  } else {
                    // When not selectable, clicking anywhere toggles the checkbox
                    onCheckboxChange(item, !item?.checked)
                  }
                }}
              >
                <div className="flex flex-1 items-center space-x-3">
                  <div
                    onClick={e => {
                      e.stopPropagation()
                    }}
                  >
                    <Checkbox
                      value={item?.checked || false}
                      updateValue={checked => onCheckboxChange(item, checked)}
                      label=""
                      isDark={true}
                    />
                  </div>
                  <div className="flex flex-1 justify-between pr-2">
                    <span className="text-sm font-bold">{item.label}</span>
                    {item.subLabel && (
                      <span className="ml-2 mt-[2px] text-xs font-bold text-primary-350">{item.subLabel}</span>
                    )}
                  </div>
                </div>
                {selectable && <Icon icon="chevron_right" className="text-primary-350" size="large" />}
              </div>

              {/* Separator - show after each item except the last one */}
              {separateItems && index < filteredItems?.length - 1 && <div className="my-2 border-b border-gray-700" />}
            </div>
          ))
        )}
      </div>

      {/* Pagination Controls */}
      {showPagination && totalPages > 1 && (
        <>
          <div className="mt-4 flex items-center justify-center pt-4">
            <Pagination
              pageCount={totalPages}
              currentPage={currentPage + 1}
              onUpdate={newPage => onPageChange(newPage - 1)}
            />
          </div>
        </>
      )}
    </div>
  )
}

export default SelectableList
